import { call } from '@everlutionsk/helpers';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import React, { ReactNode, useMemo } from 'react';
import { isDarkMode } from '../../theme';

/**
 * Primary UI component for user interaction
 */

export const badgeVariants = [
  'default',
  'highlight',
  'positive',
  'negative',
  'notice',
  'warning',
  'alert'
] as const;

type Variants = Record<(typeof badgeVariants)[number], SxProps<Theme>>;

const badgeStyles: SxProps<Theme> = {
  height: 'auto',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: 3,
  p: theme => theme.spacing(2, 3),
  borderRadius: theme => theme.radius.full,
  maxHeight: '20px'
};

interface BadgeProps {
  /**
   * Variant of badge
   * */
  readonly variant?: (typeof badgeVariants)[number];
  /**
   * Notification value inside badge
   * */
  readonly notification: number | string;
  /**
   * Custom styles
   * @default ''
   * */
  readonly sx?: SxProps<Theme>;
  /**
   * Optional icon to display at the start of the badge
   */
  readonly icon?: ReactNode;
  /**
   * Uppercase text
   */
  readonly isUppercase?: boolean;
  /**
   * Typography variant for the label. Defaults to the very small
   * `subheading2XSmall` (10px) used by notification counts.
   */
  readonly textVariant?: string;
}

export function Badge({
  variant = 'default',
  notification,
  icon,
  isUppercase = false,
  textVariant = 'subheading2XSmall',
  sx
}: BadgeProps) {
  const isModeDark = isDarkMode();

  const processedNotification = call((): string => {
    if (typeof notification === 'string') return notification.trim();

    return notification.toLocaleString();
  });

  const variantsValues: Variants = {
    default: {
      background: 'transparent'
    },
    highlight: {
      background: '#4F76FF'
    },
    positive: {
      background: '#1ABC9C'
    },
    negative: {
      background: call((): string => (isModeDark ? '#FB7572' : '#DA5B5B'))
    },
    warning: {
      background: call((): string => (isModeDark ? '#FEDA7B' : '#DFAB22'))
    },
    notice: {
      background: call((): string => (isModeDark ? '#F2FF67' : '#B0BF00'))
    },
    alert: {
      background: theme =>
        call((): string => (isModeDark ? '#FFA77B' : `${theme.brandPalette.data.dataOrange}`))
    }
  };

  const dynamicBorderStyle: SxProps<Theme> =
    variant === 'default'
      ? {
          border: isModeDark ? '1px solid #5A5A69' : '1px solid #DCDCE6'
        }
      : {};

  const textColor = call((): string => {
    if (variant === 'default') return isModeDark ? '#FFFFFF' : '#000000';

    return isModeDark ? '#000000' : '#FFFFFF';
  });

  const mergedStyles: SxProps<Theme> = useMemo<SxProps<Theme>>(
    () => ({
      ...Object.assign({}, badgeStyles, variantsValues[variant], dynamicBorderStyle, sx)
    }),
    [badgeStyles, variantsValues, dynamicBorderStyle, sx]
  );

  return (
    <Box sx={() => ({ ...mergedStyles })}>
      {icon != null && (
        <Box component="span" gap={3}>
          {icon}
        </Box>
      )}
      <Typography
        variant={textVariant as any}
        component="p"
        sx={{ color: textColor, textTransform: isUppercase ? 'uppercase' : 'none' }}
      >
        {processedNotification}
      </Typography>
    </Box>
  );
}

// Convenience wrapper for the common "status pill" case: maps the MUI-style
// tone names the admin pages already compute (success/error/warning/info/
// default) onto Badge variants, and constrains the badge to its content (the
// admin theme otherwise stretches MuiChip to minWidth: 200). Use this instead
// of <Chip> for read-only status/label indicators.
export type BadgeTone = 'success' | 'error' | 'warning' | 'info' | 'default';

const toneToVariant: Record<BadgeTone, (typeof badgeVariants)[number]> = {
  success: 'positive',
  error: 'negative',
  warning: 'warning',
  info: 'highlight',
  default: 'default'
};

export function StatusBadge({
  tone = 'default',
  label,
  isUppercase = false,
  sx
}: {
  readonly tone?: BadgeTone;
  readonly label: string;
  readonly isUppercase?: boolean;
  readonly sx?: SxProps<Theme>;
}) {
  return (
    <Badge
      variant={toneToVariant[tone]}
      notification={label}
      isUppercase={isUppercase}
      // Bump from the 10px notification-count default to 12px so status pills
      // read at a comparable size to the surrounding table/body text.
      textVariant="subheadingXSmall"
      sx={{ width: 'fit-content', maxHeight: 'unset', py: 1, ...sx }}
    />
  );
}
